import { environment } from './environment';

/**
 * Configuración de la app resuelta al arrancar el contenedor.
 *
 * Los cuatro flujos de siempre (`ng serve`, `build:dev`, `build:qa`, `build:np`,
 * `build:prod`) no cambian: el objeto `environment` compilado por `fileReplacements`
 * sigue siendo la verdad. Esto sólo lo **sobrescribe** cuando la imagen de Docker se
 * arranca con `CUSTOM_ENVIRONMENT` y el entrypoint dejó un `env.json` al lado del
 * index. Sin ese archivo, o si viene vacío, no pasa nada.
 *
 * Por eso una sola imagen sirve para todos los entornos: cambiar un endpoint es
 * reiniciar el contenedor con otro `--env-file`, no reconstruir.
 */

/** Cómo se interpreta el valor de una variable antes de escribirlo. */
type TipoValor = 'texto' | 'booleano';

interface Mapeo {
  /** Ruta dentro del objeto `environment`, separada por puntos. */
  ruta: string;
  tipo: TipoValor;
}

/**
 * Lista blanca de variables que pueden llegar al navegador.
 *
 * Es explícita **a propósito**: `env.json` se sirve como recurso público, así que
 * volcar el entorno entero del contenedor filtraría cualquier secreto que haya ahí.
 * Sólo viaja lo que está en esta tabla.
 *
 * Debe coincidir con la lista del entrypoint (`docker/entrypoint.sh`);
 * `scripts/verificar-env-runtime.mjs` falla si divergen.
 */
export const MAPEO_ENV: Readonly<Record<string, Mapeo>> = {
  PRODUCTION: { ruta: 'production', tipo: 'booleano' },
  API_URL: { ruta: 'api', tipo: 'texto' },
  SADI_FILES_API_BASE: { ruta: 'sadiFilesApiBase', tipo: 'texto' },
  STORE_IDENTIFIER_API: { ruta: 'storeIdentifierApi', tipo: 'texto' },
  APP_VERSION: { ruta: 'appVersion', tipo: 'texto' },
  PIVI_BASE_URL: { ruta: 'piviBaseUrl', tipo: 'texto' },
  PIVI_CHANNEL_ID: { ruta: 'piviChannelId', tipo: 'texto' },

  // --- featureFlags ------------------------------------------------------
  FF_ENABLE_NEW_FEATURE: {
    ruta: 'featureFlags.enableNewFeature',
    tipo: 'booleano',
  },
  FF_ENABLE_BETA_FEATURES: {
    ruta: 'featureFlags.enableBetaFeatures',
    tipo: 'booleano',
  },
  FF_ENABLE_BACKEND_FLAT_FILE_UPLOAD: {
    ruta: 'featureFlags.enableBackendFlatFileUpload',
    tipo: 'booleano',
  },
  FF_RECAUDOS_DEDUPE: {
    ruta: 'featureFlags.recaudosDedupe',
    tipo: 'booleano',
  },

  FF_CONCILIATIONS_SHOW_HUB: {
    ruta: 'featureFlags.conciliations.showHub',
    tipo: 'booleano',
  },
  FF_CONCILIATIONS_DEFAULT_MODULE: {
    ruta: 'featureFlags.conciliations.defaultModule',
    tipo: 'texto',
  },
  FF_CONCILIATIONS_EXECUTION_CONSOLIDATION: {
    ruta: 'featureFlags.conciliations.executionConsolidation',
    tipo: 'booleano',
  },

  FF_CONCILIATIONS_MODULES_CASH: {
    ruta: 'featureFlags.conciliations.modules.cash',
    tipo: 'booleano',
  },
  FF_CONCILIATIONS_MODULES_BANK: {
    ruta: 'featureFlags.conciliations.modules.bank',
    tipo: 'booleano',
  },
  FF_CONCILIATIONS_MODULES_PLATFORMS: {
    ruta: 'featureFlags.conciliations.modules.platforms',
    tipo: 'booleano',
  },
  FF_CONCILIATIONS_MODULES_COLLECTIONS: {
    ruta: 'featureFlags.conciliations.modules.collections',
    tipo: 'booleano',
  },
};

/** Archivo que escribe el entrypoint, servido junto al index. */
const ARCHIVO_ENV = 'env.json';

/** Tope de espera. Un fichero local no debería tardar, pero no puede colgar el arranque. */
const TIMEOUT_MS = 2000;

/** De dónde salió la configuración con la que arrancó la app. */
export type OrigenEntorno = 'compilado' | 'runtime';

let origen: OrigenEntorno = 'compilado';

export function origenEntorno(): OrigenEntorno {
  return origen;
}

/**
 * Lee `env.json` y sobrescribe `environment` **antes** de que arranque Angular.
 *
 * El orden importa: varios servicios capturan `environment.api` en una constante de
 * módulo al evaluarse, así que esto tiene que correr antes de importar `app.config`
 * (ver `src/main.ts`). Nunca lanza: un fallo deja la configuración compilada, que es
 * exactamente lo que quieren los despliegues que no usan Docker.
 */
export async function cargarEntornoRuntime(): Promise<void> {
  const crudo = await leerArchivo();
  if (!crudo) return;

  const aplicadas = aplicarVariables(crudo);
  if (aplicadas === 0) return;

  origen = 'runtime';
  if (!environment.production) {
    console.info(
      `[entorno] ${aplicadas} variables aplicadas desde ${ARCHIVO_ENV}`,
    );
  }
}

/** Escribe en `environment` las variables reconocidas. Devuelve cuántas aplicó. */
export function aplicarVariables(
  variables: Record<string, unknown>,
  destino: Record<string, unknown> = environment as unknown as Record<
    string,
    unknown
  >,
): number {
  let aplicadas = 0;

  for (const [nombre, valorCrudo] of Object.entries(variables)) {
    const mapeo = MAPEO_ENV[nombre];
    // Una variable fuera de la lista blanca se ignora en silencio: puede ser
    // cualquier cosa del entorno del contenedor y no tiene por qué entrar aquí.
    if (!mapeo) continue;

    const valor = interpretar(valorCrudo, mapeo.tipo);
    if (valor === undefined) continue;

    if (escribirEnRuta(destino, mapeo.ruta, valor)) aplicadas++;
  }

  return aplicadas;
}

/**
 * Convierte el valor al tipo que declara el mapeo. Devuelve `undefined` cuando el
 * valor no sirve, para dejar el compilado en su sitio en vez de pisarlo con basura.
 */
function interpretar(
  valor: unknown,
  tipo: TipoValor,
): string | boolean | undefined {
  if (valor === null || valor === undefined) return undefined;

  if (tipo === 'booleano') {
    if (typeof valor === 'boolean') return valor;
    const texto = String(valor).trim().toLowerCase();
    if (['true', '1', 'yes', 'si', 'sí'].includes(texto)) return true;
    if (['false', '0', 'no', ''].includes(texto)) return false;
    return undefined;
  }

  const texto = String(valor).trim();
  // Una cadena vacía significa "no configurado", no "bórralo".
  return texto.length > 0 ? texto : undefined;
}

/** Escribe `valor` en `ruta` sólo si la ruta ya existe en el objeto. */
function escribirEnRuta(
  destino: Record<string, unknown>,
  ruta: string,
  valor: string | boolean,
): boolean {
  const partes = ruta.split('.');
  const ultima = partes.pop();
  if (!ultima) return false;

  let nodo: Record<string, unknown> = destino;
  for (const parte of partes) {
    const siguiente = nodo[parte];
    if (typeof siguiente !== 'object' || siguiente === null) return false;
    nodo = siguiente as Record<string, unknown>;
  }

  // Crear claves que el environment compilado no tiene sería inventar
  // configuración que ningún código lee. Mejor no escribirla.
  if (!(ultima in nodo)) return false;

  nodo[ultima] = valor;
  return true;
}

async function leerArchivo(): Promise<Record<string, unknown> | null> {
  if (typeof fetch !== 'function' || typeof document === 'undefined') {
    return null;
  }

  const url = new URL(ARCHIVO_ENV, document.baseURI).toString();
  const cancelacion = AbortSignal.timeout(TIMEOUT_MS);

  try {
    // `cache: 'no-store'`: una copia vieja apuntaría al backend equivocado, que es
    // peor que no tener configuración.
    const respuesta = await fetch(url, {
      cache: 'no-store',
      signal: cancelacion,
    });
    if (!respuesta.ok) return null;

    const cuerpo: unknown = await respuesta.json();
    if (!cuerpo || typeof cuerpo !== 'object' || Array.isArray(cuerpo)) {
      return null;
    }

    return cuerpo as Record<string, unknown>;
  } catch {
    // Sin archivo (ng serve), con JSON roto o con la red caída: manda el compilado.
    return null;
  }
}
